// App.js - Main application component with stream-based calculations

import React, { useState } from 'react';
import { COMPONENTS } from './fluid-properties';
import PlantModel from './stream-based-calculations';
import FeedCompositionInput from './components/FeedCompositionInput';
import ProcessUnitSelector from './components/ProcessUnitSelector';
import PlantConfiguration from './components/PlantConfiguration';
import EnergyConsumptionResults from './components/EnergyConsumptionResults';
import ProcessFlowDiagram from './components/ProcessFlowDiagram';
import StreamCompositionView from './components/StreamCompositionView';
import PipelineHoldupTool from './components/PipelineHoldupTool';
import PipelineCalculator from './components/PipelineCalculator';
import VesselTools from './components/VesselTools';
import ReciprocatingCompressorCalculator from './components/ReciprocatingCompressorCalculator';
import CentrifugalCompressorCalculator from './components/CentrifugalCompressorCalculator';
import ProcessCanvas from './components/ProcessCanvas';
import './App.css';

function App() {
  const [activeTab, setActiveTab] = useState('plant'); // 'plant' | 'pipeline' | 'piping' | 'vessel' | 'compressor'

  // Initial gas composition state (default to a typical natural gas composition)
  const [feedComposition, setFeedComposition] = useState({
    [COMPONENTS.METHANE.name]: 85.0,
    [COMPONENTS.ETHANE.name]: 8.0,
    [COMPONENTS.PROPANE.name]: 3.0,
    [COMPONENTS.BUTANE.name]: 1.0,
    [COMPONENTS.PENTANE.name]: 0.5,
    [COMPONENTS.HEXANE.name]: 0.2,
    [COMPONENTS.NITROGEN.name]: 1.0,
    [COMPONENTS.CO2.name]: 1.0,
    [COMPONENTS.H2S.name]: 0.1,
    [COMPONENTS.WATER.name]: 0.2,
  });

  // Initial feed conditions
  // Update the initial feed conditions in the state
  const [feedConditions, setFeedConditions] = useState({
    temperature: 25,       // °C
    pressure: 60,          // barg
    flowRate: 1000,        // e3m³/d at STP
    flowRateKmol: 1760,    // kmol/h (calculated from e3m³/d)
    salesPressure: 80      // barg (sales gas delivery pressure)
  });

  // Plant configuration - which units are active
  const [activeUnits, setActiveUnits] = useState({
    fieldCompression: true,
    inletSeparation: true,
    inletCompression: true,
    stabilisation: true,
    wasteWaterDisposal: false,
    sweetening: true,
    dehydration: true,
    dewpoint: true,
    fractionation: true,
    salesCompression: true,
    utilities: 'rankine', // 'rankine', 'heatMedium', or 'none'
  });

  // Configuration parameters for each unit
  const [unitConfigurations, setUnitConfigurations] = useState({
    fieldCompression: {
      dischargePresure: 80, // bar
      efficiency: 0.75,     // fractional
      stageCount: 1,        // number of compression stages
      intercoolerTemp: 40,  // °C
      compressorType: 'centrifugal',
      driverType: 'electricMotor',
    },
    inletSeparation: {
      temperature: 20,     // °C
      pressureDrop: 0.5,   // bar
      separatorType: 'threePhase',
      separatorCount: 1,
      heatingRequired: false,
    },
    inletCompression: {
      dischargePresure: 90, // bar
      efficiency: 0.75,     // fractional
      stageCount: 2,        // number of compression stages
      intercoolerTemp: 40,  // °C
      compressorType: 'centrifugal',
      driverType: 'electricMotor',
      pressureRatio: 2.5,
    },
    stabilisation: {
      stabiliserType: 'column',
      temperature: 80,     // °C
      pressure: 5,         // bar
      trayCount: 15,
      refluxRatio: 1.5,
      heatingMedium: 'steam',
      coolingMedium: 'air',
      rvpTarget: 10,       // psi
    },
    wasteWaterDisposal: {
      disposalMethod: 'reinjection',
      injectionPressure: 150, // bar
      pumpEfficiency: 0.75,
      waterFlowRate: 20,      // m³/h
      dissolvedSolids: 35000, // ppm
      chemicalTreatment: true,
    },
    sweetening: {
      solventType: 'mdea',
      h2sInlet: 1000,         // ppm
      co2Inlet: 3,            // mol%
      h2sOutlet: 4,           // ppm
      co2Outlet: 2,           // mol%
      solventRate: 100,       // m³/h
      absorberPressure: 70,   // bar
      regeneratorPressure: 1.5, // bar
      reboilerTemp: 120,      // °C
      acidGasDisposal: 'sru',
    },
    dehydration: {
      dehydrationMethod: 'glycol',
      inletWaterContent: 120, // lb/MMscf
      outletWaterSpec: 4,     // lb/MMscf
      glycolType: 'teg',
      glycolRate: 25,         // L/kg H2O
      regenerationMethod: 'standard',
      reboilerTemp: 204,      // °C
      pressureDrop: 0.5,      // bar
    },
    dewpoint: {
      processType: 'refrigeration',
      dewpointSpec: -10,      // °C
      ethaneRecovery: 80,     // %
      propaneRecovery: 95,    // %
      refrigerantType: 'propane',
      refrigerationTemp: -35, // °C
      compressorEfficiency: 0.75,
      expanderEfficiency: 0.8,
      expanderOutletTemp: -85, // °C
      recompressionRatio: 3,
      jtInletPressure: 80,    // bar
      jtOutletPressure: 30,   // bar
      precoolingTemp: 15,     // °C
      nglSpec: 'rawMix',
    },
    fractionation: {
      configuration: 'conventional',
      feedTemperature: -10,   // °C
      feedPressure: 25,       // bar
      deethanizer: true,
      deetha_pressure: 28,    // bar
      deetha_reflux: 5,
      ethane_purity: 99,      // %
      depropanizer: true,
      depro_pressure: 18,     // bar
      depro_reflux: 3,
      propane_purity: 95,     // %
      debutanizer: true,
      debuta_pressure: 8,     // bar
      debuta_reflux: 3,
      butaneSplitter: false,
      heatSource: 'steam',
      heatIntegration: 'medium',
    },
    salesCompression: {
      dischargePressure: 80,  // bar
      efficiency: 0.75,
      compressorType: 'centrifugal',
      driverType: 'gasTurbine',
      stageCount: 2,
      intercoolerTemp: 40,    // °C
      coolingTemp: 40,        // °C
      aftercoolerType: 'airCooled',
      powerSource: 'processGas',
    },
    utilities: {
      // Rankine cycle parameters
      steamCycleType: 'simple',
      boilerPressure: 60,     // bar
      boilerTemperature: 450, // °C
      condenserPressure: 0.1, // bar
      turbineEfficiency: 0.85,
      pumpEfficiency: 0.75,
      boilerEfficiency: 0.85,
      heatSource: 'naturalGas',
      powerCapacity: 10,      // MW
      
      // Heat medium parameters
      mediumType: 'hotOil',
      systemTemperature: 280, // °C
      heaterCapacity: 5000,   // kW
      heaterEfficiency: 0.85,
      circulationRate: 150,   // m³/h
      pumpPower: 75,          // kW
      heatLoss: 0.05,         // fraction
    },
  });

  // Results of calculations
  const [calculationResults, setCalculationResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Selected stream for composition view
  const [selectedStream, setSelectedStream] = useState(null);

  // Function to calculate energy consumption based on feed and configuration
  const calculateEnergyConsumption = () => {
    setIsCalculating(true);
    
    try {
      // Convert composition from mol% to mole fractions for calculations
      const normalizedComposition = {};
      const totalMol = Object.values(feedComposition).reduce((sum, val) => sum + val, 0);
      
      Object.entries(feedComposition).forEach(([component, value]) => {
        normalizedComposition[component] = value / totalMol;
      });
      
      // Use flowRateKmol for the calculation model
      const calculationConditions = {
        temperature: feedConditions.temperature,
        pressure: feedConditions.pressure,
        flowRate: feedConditions.flowRateKmol, // Use kmol/h for calculations
        salesPressure: feedConditions.salesPressure // Pass sales pressure to the model
      };
          // Create plant model instance with normalized composition
      const plantModel = new PlantModel(
        normalizedComposition,
        calculationConditions,
        activeUnits,
        unitConfigurations
      );
      
      // Perform calculations with error handling
      console.log("Starting plant calculations...");
      const results = plantModel.calculate();
      console.log("Calculation complete:", results);
      
      // Make sure the results object has the expected structure
      const safeResults = {
        totalEnergyConsumption: results?.totalEnergyConsumption || 0,
        unitEnergyConsumption: results?.unitEnergyConsumption || {},
        totalCO2Emissions: results?.totalCO2Emissions || 0,
        specificEnergyConsumption: results?.specificEnergyConsumption || 0,
        stageCompositions: results?.stageCompositions || {},
        streams: results?.streams || {}
      };
      
      // Update results state
      setCalculationResults(safeResults);
      
      // Show the first available stream composition if any
      if (safeResults.streams && Object.keys(safeResults.streams).length > 0) {
        setSelectedStream(Object.keys(safeResults.streams)[0]);
      }
    } catch (error) {
      console.error("Error in calculation:", error);
      // Set minimal results to prevent UI errors
      setCalculationResults({
        totalEnergyConsumption: 0,
        unitEnergyConsumption: {},
        totalCO2Emissions: 0,
        specificEnergyConsumption: 0,
        stageCompositions: {},
        streams: {}
      });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Natural Gas Processing Plant Energy Model</h1>
        <nav className="app-tab-nav">
          <button
            className={`app-tab-btn${activeTab === 'plant' ? ' active' : ''}`}
            onClick={() => setActiveTab('plant')}
          >⚙ Plant Energy Model</button>
          <button
            className={`app-tab-btn${activeTab === 'pipeline' ? ' active' : ''}`}
            onClick={() => setActiveTab('pipeline')}
          >🔧 Pipeline Holdup</button>
          <button
            className={`app-tab-btn${activeTab === 'piping' ? ' active' : ''}`}
            onClick={() => setActiveTab('piping')}
          >📐 Pipeline Calc</button>
          <button
            className={`app-tab-btn${activeTab === 'vessel' ? ' active' : ''}`}
            onClick={() => setActiveTab('vessel')}
          >🛢 Vessel Tools</button>
          <button
            className={`app-tab-btn${activeTab === 'compressor' ? ' active' : ''}`}
            onClick={() => setActiveTab('compressor')}
          >⚡ Recip Compressor</button>
          <button
            className={`app-tab-btn${activeTab === 'centrifugal' ? ' active' : ''}`}
            onClick={() => setActiveTab('centrifugal')}
          >🌀 Centrifugal Comp</button>
          <button
            className={`app-tab-btn${activeTab === 'canvas' ? ' active' : ''}`}
            onClick={() => setActiveTab('canvas')}
          >🗺 Process Canvas</button>
        </nav>
      </header>

      {activeTab === 'pipeline' ? (
        <main className="app-main app-main--pipeline">
          <PipelineHoldupTool />
        </main>
      ) : activeTab === 'piping' ? (
        <main className="app-main app-main--pipeline">
          <div className="app-section">
            <h2>Pipeline Pressure Drop &amp; Hydrate Analysis</h2>
            <PipelineCalculator feedComposition={feedComposition} />
          </div>
        </main>
      ) : activeTab === 'vessel' ? (
        <main className="app-main app-main--pipeline">
          <div className="app-section">
            <h2>Vessel Design Tools</h2>
            <VesselTools />
          </div>
        </main>
      ) : activeTab === 'compressor' ? (
        <main className="app-main app-main--pipeline">
          <div className="app-section">
            <h2>Reciprocating Compressor Simulator</h2>
            <ReciprocatingCompressorCalculator initialComposition={feedComposition} />
          </div>
        </main>
      ) : activeTab === 'centrifugal' ? (
        <main className="app-main app-main--pipeline">
          <div className="app-section">
            <h2>Centrifugal Compressor &amp; Gas Turbine Simulator</h2>
            <CentrifugalCompressorCalculator initialComposition={feedComposition} />
          </div>
        </main>
      ) : activeTab === 'canvas' ? (
        <main style={{ padding: 0 }}>
          <ProcessCanvas />
        </main>
      ) : (
      <main className="app-main">
        <div className="app-section">
          <h2>Feed Composition and Conditions</h2>
          <FeedCompositionInput 
            composition={feedComposition} 
            setComposition={setFeedComposition} 
            conditions={feedConditions}
            setConditions={setFeedConditions}
          />
        </div>
        
        <div className="app-section">
          <h2>Process Configuration</h2>
          <ProcessUnitSelector 
            activeUnits={activeUnits} 
            setActiveUnits={setActiveUnits} 
          />
        </div>
        
        <div className="app-section">
          <h2>Unit Configuration</h2>
          <PlantConfiguration 
            activeUnits={activeUnits}
            configurations={unitConfigurations}
            setConfigurations={setUnitConfigurations}
          />
        </div>
        
        <div className="app-section">
          <button 
            className="calculate-button" 
            onClick={calculateEnergyConsumption}
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate Energy Consumption'}
          </button>
        </div>
        
        {calculationResults && (
          <>
            <div className="app-section results-section">
              <h2>Energy Consumption Results</h2>
              <EnergyConsumptionResults results={calculationResults} />
            </div>
            
            <div className="app-section">
              <h2>Process Flow Diagram</h2>
              <ProcessFlowDiagram 
                activeUnits={activeUnits}
                streams={calculationResults.streams}
                onStreamSelect={setSelectedStream}
                selectedStream={selectedStream}
              />
            </div>
            
            {selectedStream && calculationResults.streams[selectedStream] && (
              <div className="app-section">
                <h2>Stream Composition: {selectedStream}</h2>
                <StreamCompositionView 
                  stream={calculationResults.streams[selectedStream]}
                />
              </div>
            )}
            
            <div className="app-section">
              <h2>Stage-by-Stage Composition</h2>
              <div className="stage-selector">
                <label>Select Processing Stage:</label>
                <select 
                  onChange={(e) => setSelectedStream(e.target.value)}
                  value={selectedStream || ''}
                >
                  {Object.keys(calculationResults.stageCompositions).map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>
              
              {selectedStream && calculationResults.stageCompositions[selectedStream] && (
                <div className="stage-compositions">
                  {Object.entries(calculationResults.stageCompositions[selectedStream]).map(([streamType, composition]) => (
                    <div key={streamType} className="composition-card">
                      <h3>{streamType}</h3>
                      <table className="composition-table">
                        <thead>
                          <tr>
                            <th>Component</th>
                            <th>Mol%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(composition).map(([component, value]) => (
                            value > 0.001 && (
                              <tr key={component}>
                                <td>{component}</td>
                                <td>{(value * 100).toFixed(2)}%</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      )} {/* end activeTab === 'plant' */}

      <footer className="app-footer">
        <p>Gas Processing Plant Energy Model</p>
      </footer>
    </div>
  );
}

export default App;